import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Typography,
  Spin,
  Divider,
  Tag,
  Pagination,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import axios from "../../../utils/api";
import moment from "moment";
import { motion } from "framer-motion";
import { primaryColor } from "../../../utils/constants";

const { Title: AntTitle, Text } = Typography;

function formatSecondsToTime(seconds) {
  const duration = moment.duration(seconds, "seconds");
  const hours = String(duration.hours()).padStart(2, "0");
  const minutes = String(duration.minutes()).padStart(2, "0");
  const secs = String(duration.seconds()).padStart(2, "0");

  return `${hours}:${minutes}:${secs}`;
}
const EarningsDashboard = () => {
  const columns = [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>USER NAME</span>
        </div>
      ),
      dataIndex: "userName",
      key: "userName",
      width: "25%",
      render: (userName) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
            style={{ background: `${primaryColor}10` }}
          >
            <UserOutlined style={{ color: primaryColor, fontSize: "14px" }} />
          </div>
          <Text strong className="text-gray-800">
            {userName}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <ClockCircleOutlined style={{ color: primaryColor }} />
          </div>
          <span>CALL DURATION</span>
        </div>
      ),
      dataIndex: "callDuration",
      key: "callDuration",
      width: "20%",
      render: (duration) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
            style={{ background: `${primaryColor}10` }}
          >
            <ClockCircleOutlined
              style={{ color: primaryColor, fontSize: "14px" }}
            />
          </div>
          <Text>{duration}</Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <DollarOutlined style={{ color: primaryColor }} />
          </div>
          <span>RATE/MIN</span>
        </div>
      ),
      dataIndex: "ratePerMinute",
      key: "ratePerMinute",
      width: "15%",
      render: (rate) => (
        <div
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
          style={{
            backgroundColor: `${primaryColor}15`,
            color: primaryColor,
            border: `1px solid ${primaryColor}30`,
          }}
        >
          ₹{rate}
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <DollarOutlined style={{ color: primaryColor }} />
          </div>
          <span>TOTAL EARNINGS</span>
        </div>
      ),
      dataIndex: "totalEarnings",
      key: "totalEarnings",
      width: "20%",
      render: (_, record) => (
        <Tooltip title="Calculated based on duration and rate">
          <div
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
            style={{
              backgroundColor: `${primaryColor}20`,
              color: primaryColor,
              border: `1px solid ${primaryColor}40`,
            }}
          >
            ₹{record.totalCost}
          </div>
        </Tooltip>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>DATE</span>
        </div>
      ),
      dataIndex: "date",
      key: "date",
      width: "20%",
      render: (date) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
            style={{ background: `${primaryColor}10` }}
          >
            <CalendarOutlined
              style={{ color: primaryColor, fontSize: "14px" }}
            />
          </div>
          <Text>{date}</Text>
        </div>
      ),
    },
  ];
  const [calllist, setCallLists] = useState([]);
  const [totalAverageTime, setTotalAverageTime] = useState({
    totalDuration: 0,
    totalEarnings: 0,
  });

  const [totalCall, setTotalCall] = useState(0);
  const [loading, setLoading] = useState(true);
  const [animateCards, setAnimateCards] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const getCallLists = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `/coin-transaction/call-list?limit=${limit}&page=${page}`
      );

      setTotalCall(data.meta?.total || 0);
      const transformedData =
        data?.data?.map((item, index) => {
          const userName = `${item.userProfileId?.firstName} ${item?.userProfileId?.lastName}`;
          const callDuration = formatSecondsToTime(item.totalDuration);
          const ratePerMinute = item.perMinuteCoin;
          const totalEarnings = callDuration * ratePerMinute;
          const month = moment(item.createdAt).format("DD-MM-YYYY");

          return {
            key: `${page}-${index + 1}`,
            userName,
            callDuration,
            ratePerMinute,
            totalEarnings,
            date: month,
            totalCost: item.totalCost,
          };
        }) || [];

      setCallLists(transformedData);
      setCurrentPage(page);
      setPageSize(limit);
    } catch (error) {
      console.error("Error fetching call lists:", error);
      setCallLists([]);
      setTotalCall(0);
    } finally {
      setLoading(false);
    }
  };

  const getTotalAverageTime = async () => {
    try {
      const { data } = await axios.get("/coin-transaction/average-duration");
      setTotalAverageTime(data);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getCallLists();
    getTotalAverageTime();
  }, []);

  // Trigger animation after component mounts
  useEffect(() => {
    setTimeout(() => {
      setAnimateCards(true);
    }, 100);
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className=" sm:p-6 lg:p-8"
      style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 sm:mb-8 block  md:hidden"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
          <div className="flex items-center mb-2 sm:mb-0 p-2">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-full mr-4"
              style={{ background: `${primaryColor}15` }}
            >
              <DollarOutlined style={{ fontSize: 24, color: primaryColor }} />
            </div>
            <div>
              <AntTitle
                level={2}
                style={{ margin: 0, color: primaryColor, fontSize: "1.5rem" }}
                className="sm:text-3xl"
              >
                My Earnings Dashboard
              </AntTitle>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Metrics Cards */}
      <Row gutter={[16, 16]} className="mb-4 sm:mb-6 lg:mb-8" justify="start">
        {/* Total Earnings */}
        <Col xs={8} sm={8} md={8} lg={8} xl={8}>
          <motion.div
            initial="hidden"
            animate={animateCards ? "visible" : "hidden"}
            variants={cardVariants}
            transition={{ delay: 0.1 }}
          >
            <Card
              className="earnings-card"
              bodyStyle={{ padding: "14px 10px 16px" }}
              style={{
                borderRadius: 16,
                minHeight: 150,
                border: "none",
                boxShadow: "0 8px 20px rgba(164, 1, 101, 0.10)",
                background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
              }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full mb-2 sm:mb-3"
                  style={{ background: `${primaryColor}15` }}
                >
                  <DollarOutlined
                    style={{ fontSize: "1.4rem", color: primaryColor }}
                  />
                </div>

                <Text
                  type="secondary"
                  className="text-[10px] sm:text-xs font-medium uppercase tracking-wide"
                >
                  Total Earnings
                </Text>

                <div className="mt-1">
                  <Text
                    className="text-lg sm:text-xl lg:text-2xl font-bold"
                    style={{ color: primaryColor }}
                  >
                    ₹
                    {totalAverageTime?.totalEarnings
                      ? parseFloat(totalAverageTime.totalEarnings).toFixed(2)
                      : 0}
                  </Text>
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Total Calls */}
        <Col xs={8} sm={8} md={8} lg={8} xl={8}>
          <motion.div
            initial="hidden"
            animate={animateCards ? "visible" : "hidden"}
            variants={cardVariants}
            transition={{ delay: 0.2 }}
          >
            <Card
              className="earnings-card"
              bodyStyle={{ padding: "14px 10px 16px" }}
              style={{
                borderRadius: 16,
                minHeight: 150,
                border: "none",
                boxShadow: "0 8px 20px rgba(164, 1, 101, 0.10)",
                background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
              }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full mb-2 sm:mb-3"
                  style={{ background: `${primaryColor}15` }}
                >
                  <PhoneOutlined
                    style={{ fontSize: "1.4rem", color: primaryColor }}
                  />
                </div>

                <Text
                  type="secondary"
                  className="text-[10px] sm:text-xs font-medium uppercase tracking-wide"
                >
                  Total Calls
                </Text>

                <div className="mt-1">
                  <Text
                    className="text-lg sm:text-xl lg:text-2xl font-bold"
                    style={{ color: primaryColor }}
                  >
                    {totalCall || 0}
                  </Text>
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Average Call Duration */}
        <Col xs={8} sm={8} md={8} lg={8} xl={8}>
          <motion.div
            initial="hidden"
            animate={animateCards ? "visible" : "hidden"}
            variants={cardVariants}
            transition={{ delay: 0.3 }}
          >
            <Card
              className="earnings-card"
              bodyStyle={{ padding: "14px 10px 16px" }}
              style={{
                borderRadius: 16,
                minHeight: 150,
                border: "none",
                boxShadow: "0 8px 20px rgba(164, 1, 101, 0.10)",
                background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
              }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full mb-2 sm:mb-3"
                  style={{ background: `${primaryColor}15` }}
                >
                  <ClockCircleOutlined
                    style={{ fontSize: "1.4rem", color: primaryColor }}
                  />
                </div>

                <Text
                  type="secondary"
                  className="text-[10px] sm:text-xs font-medium uppercase tracking-wide"
                >
                  Average Call Duration
                </Text>

                <div className="mt-1">
                  <Text
                    className="text-lg sm:text-xl lg:text-2xl font-bold"
                    style={{ color: primaryColor }}
                  >
                    {formatSecondsToTime(totalAverageTime.totalDuration || 0)}
                  </Text>
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Responsive Earnings Table */}
      <div
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        {/* Table Header */}
        <div
          className="py-6 px-6 border-b"
          style={{ borderColor: "rgba(164, 1, 101, 0.1)" }}
        >
          <div className="flex flex-row items-center">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full mr-4"
              style={{ background: `${primaryColor}15` }}
            >
              <DollarOutlined style={{ fontSize: 20, color: primaryColor }} />
            </div>
            <div className="mt-2 sm:mt-0">
              <AntTitle level={4} style={{ margin: 0, color: primaryColor }}>
                Call Details & Earnings
              </AntTitle>
              <Text type="secondary">{calllist.length} calls recorded</Text>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Spin
                size="large"
                tip="Loading earnings data..."
                style={{ "--ant-primary-color": primaryColor }}
              />
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table
                  dataSource={calllist}
                  columns={columns}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalCall,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total, range) =>
                      `Showing ${range[0]}-${range[1]} of ${total} calls`,
                    className: "px-6 py-4",
                    showQuickJumper: true,
                    position: ["bottomCenter"],
                  }}
                  className="custom-earnings-table"
                  rowClassName={() => "earnings-table-row"}
                  scroll={{ x: 1000 }}
                  onChange={(pagination) => {
                    const { current, pageSize: newPageSize } = pagination;
                    getCallLists(current, newPageSize);
                  }}
                  locale={{
                    emptyText: (
                      <div className="py-8 text-center">
                        <DollarOutlined
                          style={{ fontSize: 32, color: "#ccc" }}
                        />
                        <p className="mt-2 text-gray-500">
                          No earnings data found
                        </p>
                      </div>
                    ),
                  }}
                />
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden p-4">
                {calllist.length > 0 ? (
                  calllist.map((record) => (
                    <Card
                      key={record.key}
                      className="mb-4 earnings-card"
                      style={{
                        borderRadius: "12px",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <Text strong style={{ color: primaryColor }}>
                          {record.userName}
                        </Text>
                        <Text type="secondary" className="text-xs">
                          {record.date}
                        </Text>
                      </div>
                      <Divider style={{ margin: "10px 0" }} />
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <Text type="secondary">Duration:</Text>{" "}
                          <Text strong>{record.callDuration}</Text>
                        </div>
                        <div>
                          <Text type="secondary">Earnings:</Text>{" "}
                          <Text strong style={{ color: primaryColor }}>
                            ₹{record.totalCost}
                          </Text>
                        </div>
                      </div>
                    </Card>
                  ))
                ) : (
                  <div className="py-8 text-center">
                    <DollarOutlined style={{ fontSize: 32, color: "#ccc" }} />
                    <p className="mt-2 text-gray-500">No earnings data found</p>
                  </div>
                )}
                {calllist.length > 0 && (
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalCall}
                    showSizeChanger={false}
                    showQuickJumper={true}
                    onChange={(page, newPageSize) =>
                      getCallLists(page, newPageSize)
                    }
                    className="mt-4 text-center"
                    position={["bottomCenter"]}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx="true">{`
        .earnings-card:hover {
          transform: translateY(-4px);
          transition: all 0.3s ease;
          box-shadow: 0 12px 40px rgba(164, 1, 101, 0.18) !important;
        }

        .custom-earnings-table .ant-table-thead > tr > th {
          background-color: #f9f9f9;
          font-weight: 600;
          color: #333;
          padding: 16px;
          border-bottom: 2px solid ${primaryColor}15;
        }

        .earnings-table-row:hover {
          background-color: ${primaryColor}05 !important;
          transition: all 0.2s ease;
        }

        .ant-table-row-expand-icon {
          color: ${primaryColor} !important;
        }

        .ant-pagination-item-active {
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-item-active a {
          color: ${primaryColor} !important;
        }

        .ant-pagination-item:hover {
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-item:hover a {
          color: ${primaryColor} !important;
        }

        .ant-select-item-option-selected {
          background-color: ${primaryColor}15 !important;
        }

        .ant-spin-dot-item {
          background-color: ${primaryColor} !important;
        }

        .ant-pagination-prev:hover .ant-pagination-item-link,
        .ant-pagination-next:hover .ant-pagination-item-link {
          color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-jump-prev:hover::after,
        .ant-pagination-jump-next:hover::after {
          color: ${primaryColor} !important;
        }

        .ant-select-selector:hover {
          border-color: ${primaryColor} !important;
        }

        .ant-select-focused .ant-select-selector {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }
      `}</style>
    </motion.div>
  );
};

export default EarningsDashboard;
